// GitHub no escritório (efeitos): festa na sala quando um PR abre ou é mergeado ou sai uma release,
// alarme quando o CI falha. Tudo é desenhado no campus por cima da cena, sem sprites novos:
// confete que cai, personagens que pulam, faixa dourada, giroflex nos cantos e chão avermelhado.
//
// Só o que acontece ao vivo anima (o servidor marca `live`). Com prefers-reduced-motion nada pisca,
// gira nem cai: a festa vira confete parado e faixa; o alarme vira uma luz vermelha fixa.

import { stableHash } from '../core/hash.js';
import { GITHUB_PARTY, type GithubSignal } from '../core/github.js';
import type { Activity, OfficeEvent } from '../core/types.js';
import { parseGithubDetail } from '../core/github.js';
import { t, type Chave } from './i18n.js';

type GithubEvent = Extract<OfficeEvent, { type: 'github' }>;

/** Quanto dura a festa. */
export const PARTY_MS = 12_000;
/** O alarme some sozinho depois disto, se ninguém ver o CI ficar verde. */
export const ALARM_MS = 10 * 60_000;
/** Fim da festa e do alarme: a faixa e o confete somem aos poucos neste tempo. */
const FADE_MS = 1_200;
/** Peças de confete por sala. */
export const CONFETTI = 64;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** O que o campus conta ao efeito sobre o mundo (a sala de um agente, o retângulo dela, onde ele está). */
export interface FxHost {
  /** Sala (Room.id) em que o agente está, ou a do projeto. */
  roomOf(agentId: string, project: string): string | undefined;
  /** Retângulo da sala em px de mundo, só se o andar dela estiver na tela. */
  rectOf(roomId: string): Rect | null;
  /** Pé do personagem em px de mundo, só se ele estiver na tela. */
  actorAt(agentId: string): { x: number; y: number } | null;
}

interface Party {
  roomId: string;
  start: number;
  until: number;
  banner: string;
  agents: Set<string>;
}

interface Alarm {
  roomId: string;
  start: number;
  until: number;
  /** Quem viu o CI falhar: leva o balão '!'. */
  seer: string;
}

const CONFETTI_COLORS = ['#ffd66b', '#ff5d73', '#4fc3f7', '#7ee081', '#c792ea', '#ffffff'] as const;

/** Peça `i` do confete no instante `t` (ms desde o início da festa), dentro de `r`. Puro: sem estado. */
export function confettiPiece(i: number, t: number, r: Rect): { x: number; y: number; w: number; h: number; color: string; flip: boolean } {
  const h = stableHash(`confete:${i}`);
  const x0 = r.x + 4 + ((h % 1000) / 1000) * (r.w - 8);
  const speed = 18 + ((h >>> 10) % 22); // px por segundo
  const span = r.h + 16;
  const fall = (((t / 1000) * speed + ((h >>> 5) % span)) % span) - 8;
  const sway = Math.sin(t / 420 + (h % 628) / 100) * 3;
  return {
    x: x0 + sway,
    y: r.y + fall,
    w: 2,
    h: 3,
    color: CONFETTI_COLORS[(h >>> 3) % CONFETTI_COLORS.length] as string,
    flip: Math.sin(t / 130 + i) > 0,
  };
}

/** Altura do pulo (px de arte) de um personagem na festa, `ms` depois do começo. 0 fora da festa. */
export function hopHeight(agentId: string, ms: number, duration = PARTY_MS): number {
  if (ms < 0 || ms > duration) return 0;
  const phase = (stableHash(agentId) % 628) / 100;
  const wave = Math.abs(Math.sin(ms / 230 + phase));
  // sobe depressa, e nos últimos FADE_MS os pulos vão baixando
  const ease = ms > duration - FADE_MS ? (duration - ms) / FADE_MS : 1;
  return wave * 5 * ease;
}

let reduceQuery: MediaQueryList | null | undefined;

function prefersReduced(): boolean {
  reduceQuery ??= typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  return reduceQuery?.matches ?? false;
}

/** Texto do aviso e do feed ('🎉 abriu o PR #12'), no idioma atual. */
export function githubTexto(signal: GithubSignal): string {
  return t(`gh.t.${signal.kind}`, { n: signal.number !== undefined ? ` #${signal.number}` : '', tag: signal.tag ? ` ${signal.tag}` : '', ramo: signal.branch ? ` (${signal.branch})` : '' });
}

/** Texto da faixa dourada ('PR #12 mergeado!'); undefined para os eventos sem faixa. */
export function githubFaixa(signal: GithubSignal): string | undefined {
  if (!GITHUB_PARTY.has(signal.kind) && signal.kind !== 'ci-pass') return undefined;
  return t(`gh.b.${signal.kind}` as Chave, { n: signal.number !== undefined ? ` #${signal.number}` : '', tag: signal.tag ? ` ${signal.tag}` : '' });
}

/** Texto de uma linha do feed de um evento do GitHub (undefined: não é um, mostra o texto normal). */
export function githubFeedTexto(activity: Activity): string | undefined {
  if (activity.kind !== 'github') return undefined;
  const signal = parseGithubDetail(activity.detail);
  return signal ? githubTexto(signal) : undefined;
}

export class GithubFx {
  private readonly parties = new Map<string, Party>();
  private readonly alarms = new Map<string, Alarm>();

  constructor(private readonly reduced: () => boolean = prefersReduced) {}

  /** Algum efeito na tela agora? Se não, o campus nem chama o resto. */
  active(now: number): boolean {
    this.expire(now);
    return this.parties.size > 0 || this.alarms.size > 0;
  }

  /** Um evento do GitHub chegou. Só o que é ao vivo anima. `agents` = quem está na sala agora. */
  onEvent(event: GithubEvent, now: number, host: FxHost, agents: readonly string[]): void {
    if (!event.live) return;
    const roomId = host.roomOf(event.agentId, event.project);
    if (!roomId) return;
    const { kind } = event.signal;
    if (kind === 'ci-fail') {
      const old = this.alarms.get(roomId);
      this.alarms.set(roomId, { roomId, start: old?.start ?? now, until: now + ALARM_MS, seer: event.agentId });
      return;
    }
    if (kind === 'ci-pass') {
      // o CI ficou verde onde havia alarme: o alarme acaba e vira festa
      if (!this.alarms.delete(roomId)) return;
    } else if (!GITHUB_PARTY.has(kind)) {
      return;
    }
    const banner = githubFaixa(event.signal) ?? '';
    this.parties.set(roomId, { roomId, start: now, until: now + PARTY_MS, banner, agents: new Set([event.agentId, ...agents]) });
  }

  /** Quanto o personagem sobe do chão agora (px de arte); 0 sem festa ou com movimento reduzido. */
  hop(agentId: string, now: number): number {
    if (!this.parties.size || this.reduced()) return 0;
    for (const p of this.parties.values()) if (p.agents.has(agentId)) return hopHeight(agentId, now - p.start, p.until - p.start);
    return 0;
  }

  /** Chão avermelhado das salas em alarme (em mundo, por baixo da mobília). */
  drawFloor(c: CanvasRenderingContext2D, now: number, host: FxHost): void {
    const reduced = this.reduced();
    for (const a of this.alarms.values()) {
      const r = host.rectOf(a.roomId);
      if (!r) continue;
      const pulse = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(now / 520);
      c.fillStyle = `rgba(214, 40, 40, ${(0.16 + 0.12 * pulse).toFixed(3)})`;
      c.fillRect(r.x, r.y, r.w, r.h);
    }
  }

  /** Confete e giroflex (em mundo, por cima de tudo). */
  drawAir(c: CanvasRenderingContext2D, now: number, host: FxHost): void {
    const reduced = this.reduced();
    for (const p of this.parties.values()) {
      const r = host.rectOf(p.roomId);
      if (!r) continue;
      const ms = now - p.start;
      c.globalAlpha = ms > p.until - p.start - FADE_MS ? Math.max(0, (p.until - now) / FADE_MS) : 1;
      // movimento reduzido: o confete fica parado, como num instantâneo da festa
      const t = reduced ? 3_000 : ms;
      for (let i = 0; i < CONFETTI; i++) {
        const piece = confettiPiece(i, t, r);
        c.fillStyle = piece.color;
        c.fillRect(Math.round(piece.x), Math.round(piece.y), piece.flip && !reduced ? 3 : piece.w, piece.h);
      }
      c.globalAlpha = 1;
    }
    for (const a of this.alarms.values()) {
      const r = host.rectOf(a.roomId);
      if (!r) continue;
      const corners: Array<[number, number, number]> = [
        [r.x + 5, r.y + 5, 0],
        [r.x + r.w - 5, r.y + 5, Math.PI],
        [r.x + 5, r.y + r.h - 5, Math.PI],
        [r.x + r.w - 5, r.y + r.h - 5, 0],
      ];
      for (const [x, y, phase] of corners) this.beacon(c, x, y, phase, now, reduced);
    }
  }

  /** Uma luz de giroflex: cúpula vermelha, brilho que alterna entre os cantos e facho que gira. */
  private beacon(c: CanvasRenderingContext2D, x: number, y: number, phase: number, now: number, reduced: boolean): void {
    // parado (movimento reduzido): luz acesa e fixa, sem facho
    const glow = reduced ? 0.6 : 0.5 + 0.5 * Math.sin(now / 160 + phase);
    if (!reduced) {
      const angle = now / 380 + phase;
      c.save();
      c.translate(x, y);
      c.rotate(angle);
      c.fillStyle = `rgba(255, 70, 70, ${(0.1 + 0.22 * glow).toFixed(3)})`;
      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(34, -9);
      c.lineTo(34, 9);
      c.closePath();
      c.fill();
      c.restore();
    }
    const grad = c.createRadialGradient(x, y, 0, x, y, 14);
    grad.addColorStop(0, `rgba(255, 80, 80, ${(0.35 + 0.4 * glow).toFixed(3)})`);
    grad.addColorStop(1, 'rgba(255, 80, 80, 0)');
    c.fillStyle = grad;
    c.fillRect(x - 14, y - 14, 28, 28);
    c.fillStyle = '#7a1212';
    c.fillRect(x - 3, y - 1, 6, 3);
    c.fillStyle = glow > 0.5 ? '#ff6b6b' : '#c62828';
    c.fillRect(x - 2, y - 4, 4, 4);
    c.fillStyle = '#ffd0d0';
    c.fillRect(x - 1, y - 3, 1, 1);
  }

  /** Faixa dourada, etiqueta do alarme e o '!' sobre quem viu (em px de tela, nítidos em qualquer zoom). */
  overlay(
    host: FxHost,
    labels: { alarm: string },
    /** dpr do campus (no celular tem teto, desempenho.ts); sem ele, o do navegador. */
    dprDoCampus: () => number = () => window.devicePixelRatio || 1,
  ): (c: CanvasRenderingContext2D, toScreen: (wx: number, wy: number) => { x: number; y: number }, now: number) => void {
    return (c, toScreen, now) => {
      const dpr = dprDoCampus();
      const reduced = this.reduced();
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      for (const p of this.parties.values()) {
        const r = host.rectOf(p.roomId);
        if (!r || !p.banner) continue;
        const ms = now - p.start;
        const total = p.until - p.start;
        const alpha = ms > total - FADE_MS ? Math.max(0, (total - ms) / FADE_MS) : 1;
        // entra crescendo em 280 ms (parado, com movimento reduzido)
        const grow = reduced ? 1 : Math.min(1, 0.6 + (ms / 280) * 0.4);
        const at = toScreen(r.x + r.w / 2, r.y + 14);
        c.globalAlpha = alpha;
        faixa(c, p.banner, at.x, at.y, dpr * grow);
        c.globalAlpha = 1;
      }
      for (const a of this.alarms.values()) {
        const r = host.rectOf(a.roomId);
        if (!r) continue;
        const at = toScreen(r.x + r.w / 2, r.y + 14);
        // com festa na mesma sala, a etiqueta desce para não cobrir a faixa
        etiqueta(c, labels.alarm, at.x, at.y + (this.parties.has(a.roomId) ? 26 * dpr : 0), dpr);
        const pos = host.actorAt(a.seer);
        if (!pos) continue;
        // acima do bloco nome + balão de atividade, para não cobrir o texto
        const head = toScreen(pos.x, pos.y - 30);
        const bob = reduced ? 0 : Math.sin(now / 240) * 2 * dpr;
        balaoExclama(c, head.x, head.y - 74 * dpr + bob, dpr);
      }
    };
  }

  private expire(now: number): void {
    for (const [k, p] of this.parties) if (now >= p.until) this.parties.delete(k);
    for (const [k, a] of this.alarms) if (now >= a.until) this.alarms.delete(k);
  }

  /** Salas com efeito (para os testes e para o campus decidir se precisa redesenhar). */
  state(): { parties: string[]; alarms: string[] } {
    return { parties: [...this.parties.keys()], alarms: [...this.alarms.keys()] };
  }
}

/** Faixa dourada com bordas e pontas de fita. */
function faixa(c: CanvasRenderingContext2D, text: string, x: number, y: number, k: number): void {
  c.font = `800 ${15 * k}px system-ui, sans-serif`;
  const w = c.measureText(text).width + 34 * k;
  const h = 24 * k;
  const grad = c.createLinearGradient(0, y - h / 2, 0, y + h / 2);
  grad.addColorStop(0, '#ffe58a');
  grad.addColorStop(1, '#e2a91f');
  // pontas da fita
  c.fillStyle = '#b07c0f';
  c.beginPath();
  c.moveTo(x - w / 2 - 9 * k, y - h / 2 + 5 * k);
  c.lineTo(x - w / 2 + 4 * k, y - h / 2 + 5 * k);
  c.lineTo(x - w / 2 + 4 * k, y + h / 2 + 5 * k);
  c.lineTo(x - w / 2 - 9 * k, y + h / 2 + 5 * k);
  c.lineTo(x - w / 2 - 3 * k, y + 5 * k);
  c.closePath();
  c.fill();
  c.beginPath();
  c.moveTo(x + w / 2 + 9 * k, y - h / 2 + 5 * k);
  c.lineTo(x + w / 2 - 4 * k, y - h / 2 + 5 * k);
  c.lineTo(x + w / 2 - 4 * k, y + h / 2 + 5 * k);
  c.lineTo(x + w / 2 + 9 * k, y + h / 2 + 5 * k);
  c.lineTo(x + w / 2 + 3 * k, y + 5 * k);
  c.closePath();
  c.fill();
  c.fillStyle = grad;
  c.fillRect(x - w / 2, y - h / 2, w, h);
  c.strokeStyle = '#8a5d00';
  c.lineWidth = Math.max(1, k * 1.5);
  c.strokeRect(x - w / 2 + 0.5, y - h / 2 + 0.5, w - 1, h - 1);
  c.fillStyle = '#4a2f00';
  c.fillText(`★ ${text} ★`, x, y + k);
}

/** Etiqueta vermelha fixa do alarme ('🚨 CI falhou'). */
function etiqueta(c: CanvasRenderingContext2D, text: string, x: number, y: number, dpr: number): void {
  c.font = `700 ${11 * dpr}px system-ui, sans-serif`;
  const w = c.measureText(text).width + 14 * dpr;
  const h = 18 * dpr;
  c.fillStyle = '#b3201f';
  c.fillRect(x - w / 2, y - h / 2, w, h);
  c.strokeStyle = '#ffb3b0';
  c.lineWidth = dpr;
  c.strokeRect(x - w / 2 + 0.5, y - h / 2 + 0.5, w - 1, h - 1);
  c.fillStyle = '#ffffff';
  c.fillText(text, x, y + dpr * 0.5);
}

/** Balão redondo com '!' sobre quem viu o CI falhar. */
function balaoExclama(c: CanvasRenderingContext2D, x: number, y: number, dpr: number): void {
  const r = 8 * dpr;
  c.fillStyle = '#d62828';
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.fill();
  c.beginPath();
  c.moveTo(x - 3 * dpr, y + r - 2 * dpr);
  c.lineTo(x - 6 * dpr, y + r + 4 * dpr);
  c.lineTo(x + 1 * dpr, y + r - 1 * dpr);
  c.fill();
  c.strokeStyle = '#ffffff';
  c.lineWidth = dpr;
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.stroke();
  c.fillStyle = '#ffffff';
  c.font = `800 ${12 * dpr}px system-ui, sans-serif`;
  c.fillText('!', x, y + dpr * 0.5);
}
